import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { BookOpen, Users } from 'lucide-react';
import { supabase } from "../lib/supabase";
import { profilePhotoService } from '../lib/services/profilePhotoService';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './notifications/NotificationBell';
import { ChangePasswordModal } from './modals/ChangePasswordModal';
import { SimpleUserManual } from './SimpleUserManual';

interface UserNavProps {
  hideAvatar?: boolean;
}

export function UserNav({ hideAvatar = false }: UserNavProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isUserManualOpen, setIsUserManualOpen] = useState(false);

  useEffect(() => {
    let active = true;

    const syncProfileState = async () => {
      if (!profile) {
        if (active) {
          setUserEmail('');
          setUserName('');
          setProfilePhoto(null);
        }
        return;
      }

      setUserEmail(profile.email ?? '');
      setUserName(profile.name ?? '');

      if (profile.profile_photo) {
        const signedUrl = await profilePhotoService.getSignedUrl(profile.profile_photo);
        if (active) {
          setProfilePhoto(signedUrl);
        }
      } else if (active) {
        setProfilePhoto(null);
      }
    };

    syncProfileState();

    return () => {
      active = false;
    };
  }, [profile?.email, profile?.name, profile?.profile_photo]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const getProfilePath = () => {
    switch (profile?.role) {
      case 'Client':
        return '/client/profile';
      case 'Vendor':
        return '/vendor/profile';
      case 'CupShup':
        return '/cupshup/profile';
      default:
        return '/';
    }
  };

  if (!profile) {
    return null;
  }

  return (
    <div className="flex items-center gap-x-2">
      {profile.role === 'CupShup' && <NotificationBell />}

      {!hideAvatar && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8 border border-gray-200 bg-white">
                {profilePhoto ? (
                  <AvatarImage src={profilePhoto} alt={userName || 'User'} />
                ) : (
                  <AvatarFallback className="bg-gradient-to-br from-[#00a979] to-[#0da5e1] text-white text-xs font-bold flex items-center justify-center w-full h-full">
                    {userName ? userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                  </AvatarFallback>
                )}
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56"
            align="end"
            sideOffset={8}
            alignOffset={-4}
          >
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{userName || 'User Name'}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {userEmail || 'user@example.com'}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => navigate(getProfilePath())}
              >
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setIsChangePasswordModalOpen(true)}
              >
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setIsUserManualOpen(true)}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                User Manual
              </DropdownMenuItem>
              {profile.role === 'CupShup' && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => navigate('/cupshup/organization-hierarchy')}
                >
                  <Users className="mr-2 h-4 w-4" />
                  Organization Hierarchy
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-red-600 focus:text-red-600"
              onClick={handleSignOut}
            >
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      {/* User Manual */}
      <SimpleUserManual
        isOpen={isUserManualOpen}
        onClose={() => setIsUserManualOpen(false)}
      />
    </div>
  );
}